import _ from 'lodash'
import {DateRangeActiveRecord} from './active_record/base'
import {t} from '@eitje/bridge'
import {date, ftime, buildDateTime} from '@eitje/utils'
import utils from '@eitje/utils'

const DAY_START_TIME = '00:00'
const DAY_END_TIME = '23:59'

const transformData = data => {
	const {kind, end_datetime, start_datetime, end_time, start_time, total_mins} = data
	const transformedData = {...data, mins: total_mins}
	const hasStartDatetime = !!start_datetime
	const hasEndDatetime = !!end_datetime
	const hasTime = !!(end_time || start_time)
	const isFewHours = kind === 'few_hours' || (!kind && hasTime)
	const isMultipleDays = kind === 'multiple_days' || (!kind && start_datetime !== end_datetime)

	// if environment_id is null, do not include it in the data
	// once leaverequests are moved to the organisation, we can revise this
	if (transformedData.environment_id == null) delete transformedData.environment_id

	if (isFewHours) {
		// Missing end_time should create a zero-minute range; otherwise makeRange rolls the end to the next day.
		const normalizedEndTime = end_time || start_time

		return {
			...transformedData,
			...(hasStartDatetime && {start_datetime: buildDateTime({date: start_datetime, time: start_time})}),
			...(hasStartDatetime && hasTime && {end_datetime: buildDateTime({date: start_datetime, time: normalizedEndTime})}),
		}
	}

	if (isMultipleDays)
		return {
			...transformedData,
			...(hasStartDatetime && {start_datetime: buildDateTime({date: start_datetime, time: DAY_START_TIME})}),
			...(hasEndDatetime && {end_datetime: buildDateTime({date: end_datetime, time: DAY_END_TIME})}),
		}

	return {
		...transformedData,
		...(hasStartDatetime && {start_datetime: buildDateTime({date: start_datetime, time: DAY_START_TIME})}),
		...(hasEndDatetime && {end_datetime: undefined}),
	}
}

class LeaveRequest extends DateRangeActiveRecord {
	static defaultJoins = ['users']
	static startDateField = 'start_datetime'
	static endDateField = 'end_datetime'
	static tableName = 'leaveRequests'
	static kinds = ['multiple_days', 'single_day', 'few_hours']

	constructor(props) {
		super(props)
		this.status = this._status()
	}

	static create(data) {
		return super.create(transformData(data))
	}

	static useShow(params = {}) {
		const item = super.useShow(params)
		if (_.isEmpty(item)) return item
		const kind = item.kind()

		if (kind === 'few_hours')
			return {
				...item,
				kind,
				end_time: item.endTime(),
				start_time: item.startTime(),
			}

		return {...item, kind}
	}

	update(data) {
		return super.update(transformData(data))
	}

	_status() {
		const {accepted, denied} = this
		if (accepted) return 'accepted'
		if (denied) return 'denied'
		if (this.isExpired()) return 'expired'
		return 'pending'
	}

	startDate() {
		return date(this.start_datetime)
	}

	endDate() {
		return date(this.end_datetime)
	}

	startTime() {
		return this.startDate().format('HH:mm')
	}

	endTime() {
		return this.endDate().format('HH:mm')
	}

	formatPeriod({year, includeDate = true, variant} = {}) {
		const kind = this.kind()
		const startDate = this.startDate()
		const endDate = this.endDate()

		if (variant === 'compact') {
			const formattedStartDate = startDate.ftime('dayMonth')
			const formattedEndDate = endDate.ftime('dayMonth')

			if (kind === 'single_day') return formattedStartDate
			if (kind === 'few_hours') {
				const period = `${this.startTime()} - ${this.endTime()}`
				return `${formattedStartDate} ${period}`
			}

			if (startDate.month() === endDate.month()) return `${startDate.date()} - ${formattedEndDate}`

			return `${formattedStartDate} - ${formattedEndDate}`
		}

		if (kind === 'single_day') return startDate.formatDate({year})
		const formatting = year ? 'dayOfWeekMonthYear' : 'dayOfWeekMonth'
		if (kind === 'multiple_days') return this.dateRange.format(formatting)

		const period = `${this.startTime()} - ${this.endTime()}`
		return includeDate ? `${ftime(startDate, formatting)}: ${period}` : period
	}

	isOnDate(date) {
		if (this.kind() !== 'few_hours') return this.dateRange.contains(date)

		return date.format() === this.dateRange.start.format()
	}

	inPast() {
		return this.endDate().isBefore(date())
	}

	isExpired() {
		return !this.isProcessed() && this.inPast()
	}

	days() {
		return this.dateRange.days() + 1 // we want this to be 't/m' and not tot, but are puzzling how to name that aptly
	}

	isProcessed() {
		const {accepted, denied} = this
		return accepted || denied
	}

	reset() {
		return this.resourceReq(`reset`)
	}

	relevantEnvironmentIds() {
		const userRelevantEnvIds = this.user?.relevantEnvIds?.()
		if (userRelevantEnvIds) return userRelevantEnvIds

		// Mobile can hold leave requests whose joined user is out of service and lacks deleted env data.
		return [this.environment_id]
	}

	toString() {
		const {start_datetime, end_datetime} = this
		if (this.kind() == 'multiple_days') return t('functions.fromTillShort', {from: start_datetime, till: end_datetime})
		return date(start_datetime).formatDate({year: true})
	}

	kind() {
		const dayMinutes = 1439
		const minutes = this.dateRange.minutes()
		if (minutes == dayMinutes) return 'single_day'
		if (minutes > dayMinutes) return 'multiple_days'
		return 'few_hours'
	}
}

export default LeaveRequest
